import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import SearchField from "../../components/SearchField/SearchField";
import { tariffesArraySelector, userSelector } from "../../store/selectors";
import plusIcon from "../../assets/plus-icon.svg";
import ProfileSidebar from "../../components/ProfileSidebar/ProfileSidebar";
import "./AdminOrganizationsScreen.css";
import momentTz from "moment-timezone";
import moment from "moment";
import PrimaryButton from "../../components/PrimaryButton/PrimaryButton";
import Avatar from "../../components/Avatar/Avatar";
import { ReactComponent as MenuIcon } from "../../assets/mobile/menu.svg";
import SortableTable from "../../components/SortableTable/SortableTable";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableSortLabel from "@mui/material/TableSortLabel";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import AppLogo from "../../assets/AppLogoBlue.svg";
import NotificationsPopup from "../../components/NotificationsPopup/NotificationsPopup";
import {
  deleteOrganizationSubscription,
  deleteTariff,
  fetchOrganizationsTariffes,
  fetchTariffes,
} from "../../store/tariffesSlice";
import AddTariffModal from "../../components/AddTariffModal/AddTariffModal";
import EditTariffModal from "../../components/EditTariffModal/EditTariffModal";
import TariffVersionsModal from "../../components/TariffVersionsModal/TariffVersionsModal";
import OrganizationChangeTariffModal from "../../components/OrganizationChangeTariffModal/OrganizationChangeTariffModal";
import ProfilePopup from "../../components/ProfilePopup/ProfilePopup";
import useIsMobile from "../../hooks/useIsMobile";
import AddUserModal from "../../components/AddUserModal/AddUserModal";
import { createUserSubscription } from "../../store/usersSlice";
import toast from "react-hot-toast";
import { OrbitProgress } from "react-loading-indicators";

const eternalTariffesIds = [8, 19, 21];

const AdminOrganizationsScreen = () => {
  const user = useSelector(userSelector);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchQuery, setSearchQuery] = useState("");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const { logout } = useAuth();
  const [testerLoadingIds, setTesterLoadingIds] = useState([]);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userName = user?.email;
  const [availableTariffesArray, setAvailableTariffesArray] = useState([]);
  const [organizationsArray, setOrganizationsArray] = useState([]);
  const [orgToEdit, setOrgToEdit] = useState(null);
  const [direction, setDirection] = useState("desc");
  const [orderBy, setOrderBy] = useState("id");
  const [isUserEmailOpened, setIsUserEmailOpened] = useState(false);
  const filteredOrganizations = organizationsArray.filter((organization) =>
    organization.organizationName
      .toLowerCase()
      .includes(searchQuery.toLowerCase())
  );

  useEffect(() => {
    document.title = "7cut • Админка";
  }, []);

  const formatDate = (dateString) => {
    const res = momentTz(dateString).tz("etc/gmt-3").format("DD.MM.YYYY");
    return res;
  };

  function descendingComparator(a, b, property) {
    if (property === "startDate") {
      return momentTz(b.startDate, "DD.MM.YYYY")
        .utc()
        .diff(momentTz(a.startDate, "DD.MM.YYYY").utc());
    }
    if (b[property] < a[property]) {
      return -1;
    }
    if (b[property] > a[property]) {
      return 1;
    }
    return 0;
  }

  const getComparator = (directionValue, orderByValue) => {
    return directionValue === "desc"
      ? (a, b) => descendingComparator(a, b, orderByValue)
      : (a, b) => -descendingComparator(a, b, orderByValue);
  };

  const prepareData = (filteredOrganizations) => {
    const mappedData = filteredOrganizations.map((organization) => ({
      id: organization.organizationId,
      organizationName: organization.organizationName,
      tariff:
        (organization.subscription && organization.subscription.plan.name) ||
        "Отсутствует",
      orgData: organization,
      startDate:
        (organization.subscription &&
          formatDate(organization.subscription.startDate)) ||
        "-",
    }));

    const sortedData = mappedData.sort(getComparator(direction, orderBy));

    return sortedData;
  };

  const organizationsTableData = prepareData(filteredOrganizations);

  useEffect(() => {
    updateInfo();
  }, []);

  const updateInfo = () => {
    dispatch(fetchOrganizationsTariffes({})).then((res) => {
      if (!res.error) {
        const orgArray = res.payload.content.sort((a, b) =>
          a.organizationId > b.organizationId ? -1 : 1
        );
        setOrganizationsArray(orgArray);
      }
    });
    dispatch(fetchTariffes({ isDeleted: false })).then((res) => {
      setAvailableTariffesArray(res.payload);
    });
  };

  const onRequestSort = (event, property) => {
    const isAsc = orderBy === property && direction === "asc";
    setDirection(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  const handleInviteUser = () => {};

  const headCells = [
    {
      id: "name",
      numeric: false,
      disablePadding: true,
      label: "Dessert (100g serving)",
    },
    {
      id: "calories",
      numeric: true,
      disablePadding: false,
      label: "Calories",
    },
    {
      id: "fat",
      numeric: true,
      disablePadding: false,
      label: "Fat (g)",
    },
    {
      id: "carbs",
      numeric: true,
      disablePadding: false,
      label: "Carbs (g)",
    },
    {
      id: "protein",
      numeric: true,
      disablePadding: false,
      label: "Protein (g)",
    },
  ];

  const onAddToTester = async (orgData) => {
    if (testerLoadingIds.includes(orgData.organizationId)) return;
    setTesterLoadingIds([...testerLoadingIds, orgData.organizationId]);
    const testerTariff = availableTariffesArray.find((tar) => tar.id === 21);

    const testerPrice =
      testerTariff &&
      testerTariff.prices &&
      testerTariff.prices.find((price) => price.periodUnit === "MONTH");

    const testerPriceId = (testerPrice && testerPrice.id) || 190;

    const trialDays = 1;
    const trialStartDate = moment().format("YYYY-MM-DD");

    const trialEndDateObject = moment().add(trialDays, "days");
    const trialEndDate = trialEndDateObject.format("YYYY-MM-DD");
    if (orgData.subscription) {
      const res2 = await dispatch(
        deleteOrganizationSubscription({
          subscriptionId: orgData.subscription.id,
        })
      );

      if (res2.error) {
        return;
      }
    }
    dispatch(
      createUserSubscription({
        organizationId: orgData.organizationId,
        subscriptionData: {
          planVersionPriceId: testerPriceId,
        },
        trialStartDate,
        trialEndDate,
        status: "TRIALING",
      })
    ).then((res) => {
      if (!res.error) {
        toast.success(
          `Активирован триал тарифа Tester для организации ${orgData.organizationName}`
        );
        updateInfo();
      } else {
        toast.error("Произошла ошибка");
      }
      setTesterLoadingIds(
        testerLoadingIds.filter((id) => id !== orgData.organizationId)
      );
    });
  };

  return (
    <div className={isMobile ? "App-mobile" : "adminScreen-App"}>
      {/* HEADER */}
      <div
        className={isMobile ? "mainScreenHeader-mobile" : "adminScreen-header"}
      >
        <div className={isMobile ? "header-left-mobile" : "header-left"}>
          {isMobile && (
            <div
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              style={{ display: "flex", marginTop: 7 }}
            >
              <MenuIcon color="white" />
            </div>
          )}
          <div
            onClick={() => navigate("/home", { replace: true })}
            className={"mainScreen-logoBlock"}
            style={{ cursor: "pointer" }}
          >
            <img src={AppLogo} style={{ height: "100%" }} alt="logo" />
          </div>
        </div>
        <div className="header-actions">
          <SearchField onValueChanged={(value) => setSearchQuery(value)} />
          <NotificationsPopup />
          <ProfilePopup />
        </div>
      </div>
      <div className="admin-container" style={{ alignItems: "flex-start" }}>
        {((!isMobile || isMenuOpen) && (
          <ProfileSidebar activeTab="admin" subActive="organizations" />
        )) ||
          null}
        <main className="admin-main">
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <h1 className="admin-title">Организации</h1>
            <PrimaryButton
              text={"+ Пригласить пользователя"}
              onClick={() => setIsUserEmailOpened(true)}
              style={{ marginLeft: "14px", padding: "6px 12px" }}
            />
            {/* <PrimaryButton
              text={"+ Оформить на юридическое лицо"}
              onClick={() => setIsUserEmailOpened(true)}
              style={{ marginLeft: "14px", padding: "6px 12px" }}
            /> */}
          </div>
          <TableContainer
            component={Paper}
            style={{
              backgroundColor: "transparent",
              color: "white",
              boxShadow: "none",
              overflowX: isMobile ? "scroll" : "hidden",
              overflowY: "scroll",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
            }}
          >
            <Table sx={{ minWidth: 650 }} aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell
                    key="id"
                    style={{ color: "white", fontWeight: "bold" }}
                    align="left"
                  >
                    id
                    <TableSortLabel
                      active={orderBy === "id"}
                      direction={orderBy === "id" ? direction : "asc"}
                      onClick={createSortHandler("id")}
                      sx={{
                        "& .MuiTableSortLabel-icon": {
                          color: "white !important",
                          height: 15,
                          marginBottom: 0.3,
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell
                    key="name"
                    style={{ color: "white", fontWeight: "bold" }}
                  >
                    Название
                    <TableSortLabel
                      active={orderBy === "organizationName"}
                      direction={
                        orderBy === "organizationName" ? direction : "asc"
                      }
                      onClick={createSortHandler("organizationName")}
                      sx={{
                        "& .MuiTableSortLabel-icon": {
                          color: "white !important",
                          height: 15,
                          marginBottom: 0.3,
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell
                    key="startDate"
                    style={{ color: "white", fontWeight: "bold" }}
                  >
                    Дата активации
                    <TableSortLabel
                      active={orderBy === "startDate"}
                      direction={orderBy === "startDate" ? direction : "asc"}
                      onClick={createSortHandler("startDate")}
                      sx={{
                        "& .MuiTableSortLabel-icon": {
                          color: "white !important",
                          height: 15,
                          marginBottom: 0.3,
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell
                    key="tariff"
                    style={{ color: "white", fontWeight: "bold" }}
                    align="left"
                  >
                    Тариф
                    <TableSortLabel
                      active={orderBy === "tariff"}
                      direction={orderBy === "tariff" ? direction : "asc"}
                      onClick={createSortHandler("tariff")}
                      sx={{
                        "& .MuiTableSortLabel-icon": {
                          color: "white !important",
                          height: 15,
                          marginBottom: 0.3,
                        },
                      }}
                    />
                  </TableCell>

                  <TableCell
                    style={{ color: "white", fontWeight: "bold" }}
                    align="left"
                  >
                    Изменить
                  </TableCell>
                  <TableCell
                    style={{ color: "white", fontWeight: "bold" }}
                    align="left"
                  >
                    Тестер
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {organizationsTableData.map((row) => (
                  <TableRow
                    key={row.id}
                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  >
                    <TableCell style={{ color: "white" }} align="left">
                      {row.id}
                    </TableCell>
                    <TableCell
                      style={{ color: "white" }}
                      component="th"
                      scope="row"
                    >
                      {row.organizationName}
                    </TableCell>
                    <TableCell style={{ color: "white" }} align="left">
                      {row.startDate}
                    </TableCell>
                    <TableCell style={{ color: "white" }} align="left">
                      {row.tariff}
                    </TableCell>
                    <TableCell style={{ color: "white" }} align="left">
                      <div
                        onClick={() => setOrgToEdit(row.orgData)}
                        style={{
                          backgroundColor: "white",
                          color: "black",
                          padding: 10,
                          borderRadius: 8,
                          width: 100,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxSizing: "border-box",
                          cursor: "pointer",
                          alignSelf: "flex-end",
                        }}
                      >
                        Изменить
                      </div>
                    </TableCell>
                    <TableCell style={{ color: "white" }} align="left">
                      <div
                        onClick={() => onAddToTester(row.orgData)}
                        style={{
                          backgroundColor: "white",
                          color: "black",
                          padding: 10,
                          borderRadius: 8,
                          width: 100,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxSizing: "border-box",
                          cursor: "pointer",
                          alignSelf: "flex-end",
                        }}
                      >
                        {testerLoadingIds.includes(
                          row.orgData.organizationId
                        ) ? (
                          <OrbitProgress
                            style={{ fontSize: 4 }}
                            color="#3496FF"
                          />
                        ) : (
                          <span>Активировать</span>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {isUserEmailOpened && (
            <AddUserModal
              isOpen={isUserEmailOpened}
              onClose={() => setIsUserEmailOpened(false)}
              onSubmit={() => {
                updateInfo();
              }}
            />
          )}
          {orgToEdit && (
            <OrganizationChangeTariffModal
              orgInfo={orgToEdit}
              tariffesArray={availableTariffesArray}
              onSubmit={() => {
                updateInfo();
                setOrgToEdit(null);
              }}
              onClose={() => setOrgToEdit(null)}
            />
          )}
        </main>
      </div>
    </div>
  );
};

export default AdminOrganizationsScreen;
